import { Inject, Injectable, inject } from '@angular/core';
import { BaseComponent } from '../../components/base/base.component'
import {DialogService, StudioFactory} from '@wms/app/services';
import { Router } from '@angular/router';
import { VcsAPIService } from '@wms/app/vcs/services/vcs-api/vcs-api.service';
import { isTextLikeFile } from '@wms/app/core';
import { IMergeConflict } from './interface/merge-conflict-response';
import { IConflictFile } from './interface/conflict-file';
import { IConflictFileDiff } from './interface/conflicted-file-diff';
import { IconflictedFilesMap } from './interface/conflicted-file-map';
import { IconflictedFilesWithBreadcrumb } from './interface/conflicted-files-with-breadcrumb';
import { replace } from 'projects/core/src/utils/utils';

@Injectable({
  providedIn: 'root'
})
export class MergeService extends BaseComponent {
  private vcsAPIService: VcsAPIService;
  private dialogService: DialogService;
  conflictedFileDiff: IConflictFileDiff | null = null
  conflictSelectedInReview: IConflictFile | null
  wsModes: string[] = ["REVIEW_MERGE", "RESOLVE_CONFLICTS"]
  wsMode: string = "REVIEW_MERGE";
  hideResources: boolean = false;
  activeComponent: string = "ALL";
  activeChangeScope: string = "CONFLICTED";
  isWSDirty: boolean = false
  conflictedFileList: IConflictFile[]
  conflictedPaths: string[];
  conflictsWithBreadcrumbs: IconflictedFilesWithBreadcrumb[]
  currentConflictIndex: number = 0
  openThreeWayDialog: boolean = false
  hidePagination: boolean
  descriptionMsg = {
    OURS: 'Ours',
    THEIRS: 'Remote'
  }
  propertyEditoractions: string[] = ["APPLY_THEIRS", "APPLY_OURS"]
  reloadFlag: boolean = false
  appBaseMetaInfo: any;
  baseURL = 'assets'
  mergeOperationType: string
  resolvedConflicts: number;
  totalConflicts: number;
  conflictDetails: IMergeConflict
  areConflictsResolved: boolean;
  commitMessage = '';
  studioFactory:StudioFactory;

  constructor(private router: Router, @Inject("APP_BUILD_META_INFO") appBaseMetaInfo: any) {
    super()
    this.appBaseMetaInfo = appBaseMetaInfo;
    this.baseURL = this.appBaseMetaInfo.CDN_BASE_URL ? this.appBaseMetaInfo.CDN_BASE_URL + this.baseURL : this.baseURL;
    this.dialogService = inject(DialogService);
    this.vcsAPIService = inject(VcsAPIService)
    this.studioFactory = inject(StudioFactory);
  }

  closeDialogWithId(id: string): void {
    this.dialogService.close(id);
  }

  openDialogWithId(id: string): void {
    this.dialogService.open(id);
  }

  reloadProject() {
    this.reloadFlag = true
    this.router.navigate(['page/Main'])
  }

  switchWsMode(mode: string) {
    if (this.wsMode === mode) return

    switch (mode) {
      case this.wsModes[0]: {  //REVIEW_MERGE
        this.wsMode = mode
        this.getMergeConflictDetails()
        this.hideResources = false
        break
      }
      case this.wsModes[1]: {  //RESOLVE_CONFLICTS
        this.wsMode = mode
        this.hideResources = true
        this.conflictedFileDiff = null
        this.conflictsWithBreadcrumbs = []
        if (this.conflictSelectedInReview) {
          this.hidePagination = true
          this.conflictedFileList = [this.conflictSelectedInReview!]
          this.conflictedPaths = [this.conflictSelectedInReview?.path as string]
          this.constructConflictObj()
        } else {
          this.hidePagination = false
          this.getMergeConflictDetails("CONFLICTED")
        }
        break
      }
    }
  }

  getMergeConflictDetails(changeScope?: string): Promise<IMergeConflict> {
    return this.vcsAPIService.getMergeConflictDetails(this.wsMode === 'RESOLVE_CONFLICTS' ? 'ALL' : this.activeComponent, changeScope ? changeScope : this.activeChangeScope).then((response: IMergeConflict): IMergeConflict => {
      if(response.conflictedPaths.length) {
        this.studioFactory.hasMergeChangesConflicts = true
      }
      this.conflictDetails = response
      this.totalConflicts = response.initialContentConflictedPaths.length + response.initialPathConflictedPaths.length
      this.resolvedConflicts = this.totalConflicts - response.conflictedPaths.length;
      this.areConflictsResolved = this.resolvedConflicts === this.totalConflicts
      this.conflictedFileList = response.entries
      this.conflictedPaths = response.conflictedPaths
      this.hidePagination = response.conflictedPaths.length === 0
      this.currentConflictIndex = 0
      this.mergeOperationType = response.mergeOperationType
      this.descriptionMsg.OURS = this.locale['LABEL_OURS_'+ this.mergeOperationType]
      this.descriptionMsg.THEIRS = this.locale['LABEL_THEIRS_'+ this.mergeOperationType]
      this.commitMessage = response.commitMessage;
      this.initWs()
      return response
    })
  }

  getFileDiff(filePath?: string) {
    this.vcsAPIService.getDiffOfConflictedFile(filePath ? filePath : this.conflictedPaths[this.currentConflictIndex]).then((response: IConflictFileDiff) => {
      this.conflictedFileDiff = response
    })
  }

  isAllowedToChangeAction(file: IConflictFile, isAllowedToChange: boolean) {
    file.isAllowedToChange = isAllowedToChange;
    if (file.pathType === 'DIR' && file.children && file.children.length) {
      file.children.forEach(childFile => {
        var canBeChanged = file.selectedAction === "MERGE_INNER_FILES" && isAllowedToChange;
        this.isAllowedToChangeAction(childFile, canBeChanged);
        file.dirty = (file.selectedAction === "MERGE_INNER_FILES" && file.selectedAction !== file.defaultMergeAction) ? true : this.setDirty(file.children, file.selectedAction!, file.mergeActionsMap[file.selectedAction!])
      });

    } else if (file.pathType == 'FILE') {
      file.dirty = (file.selectedAction !== file.defaultMergeAction && !['RESOLVE_CONFLICTS', 'RE_RESOLVE_CONFLICTS'].includes(file.selectedAction!)) ? true : false
    }
    this.isWSDirty = this.isDirty()
  }

  private isDirty() {
    const files = this.wsMode === 'REVIEW_MERGE' ? this.conflictedFileList : this.conflictsWithBreadcrumbs?.[this.currentConflictIndex]?.files!;
    this.isWSDirty = this.setDirty(files, "MERGE_INNER_FILES", []);
    return this.isWSDirty;
  }

  private setDirty(files: IConflictFile[], parentSelectedAction: string, parentSelectedActionEquivalent: string[]) {
    var dirty = false;
    files?.forEach(file => {
      file.dirty = false;
      let fileAction = file.selectedAction as string,
        fileActionEquivalent = file.mergeActionsMap[fileAction];

      if (parentSelectedAction !== "MERGE_INNER_FILES") {
        fileAction = parentSelectedAction;
        fileActionEquivalent = parentSelectedActionEquivalent;
      }
      if (fileAction && file.defaultMergeAction !== fileAction && !this.isSameAction(file.mergeActionsMap[file.defaultMergeAction] || [], fileActionEquivalent || []) && !['RESOLVE_CONFLICTS', 'RE_RESOLVE_CONFLICTS'].includes(fileAction)) {
        file.dirty = true;
      }
      if (file.pathType === 'DIR' && file.children && file.children.length && this.setDirty(file.children, fileAction, fileActionEquivalent)) {
        file.dirty = true;
      }

      dirty = dirty || file.dirty;
    });
    return dirty;
  }

  private isSameAction(list1: string[], list2: string[]) {
    var combinedList = [...list1, ...list2];
    return [...new Set(combinedList)].length < combinedList.length;
  }

  private initWs() {
    const sourceFiles = this.conflictedFileList
    this.addProperties(sourceFiles)
    sourceFiles.forEach(file => {
      this.constructTree(file)
      this.isAllowedToChangeAction(file, true)
    })
    if (this.wsMode === 'RESOLVE_CONFLICTS' && this.conflictedPaths?.length)
      this.constructConflictObj()
  }

  private addProperties(sourceFiles: IConflictFile[]) {
    for (const file of sourceFiles) {
      file.selectedAction = file.defaultMergeAction
      file.supportedMergeActions = Object.keys(file.mergeActionsMap);
      if (file.pathType === 'FILE') {
        file.showdiff = isTextLikeFile(file.name) && !['CONTENT_CONFLICTED', 'PATH_CONFLICTED'].includes(file.latestBranchInType);
      }
      if (file.children && file.children.length > 0) {
        this.addProperties(file.children)
      }
    }
  }


  private constructConflictObj() {
    let conflictedFilesMap: IconflictedFilesMap = {}
    const transformedConflicts = []

    const collectConflictedPathRootFile = (file: IConflictFile, rootFile: IConflictFile) => {
      if (this.conflictedPaths.includes(file.path)) {
        conflictedFilesMap[file.path] = rootFile;
      } else if (file.children) {
        file.children.forEach(childFile => {
          collectConflictedPathRootFile(childFile, rootFile);
        });
      }
    }
    this.conflictedFileList.forEach(file => {
      collectConflictedPathRootFile(file, file);
    });

    for (let path of this.conflictedPaths) {
      const file = conflictedFilesMap[path];
      const parts = path.split('/');
      const nonClickableParts = file.path.split('/');
      const nonClickableIndex =
        parts.length === nonClickableParts.length && parts.length === 1 ? 0 : file.path.split('/').length - 2;

      let breadcrumb = [parts.slice(0, nonClickableIndex + 1).join('/'), ...parts.slice(nonClickableIndex + 1)];
      const breadcrumbs = breadcrumb.map((value, index, arr) => {
        return {
          label: value,
          path: arr.slice(0, index + 1).join('/'),
        };
      });

      // Determine if 'hasMergeInnerFiles' is true based on conditions
      const hasMergeInnerFiles =
        (file.pathType === 'DIR' && file.defaultMergeAction === 'MERGE_INNER_FILES') || file.pathType === 'FILE';

      // Create an object and push it into the 'transformed conflicts' array
      transformedConflicts.push({
        path: path,
        filesCopy: [{ ...file }],
        breadcrumbs: breadcrumbs,
        hasMergeInnerFiles: hasMergeInnerFiles,
      });
    }
    this.conflictsWithBreadcrumbs = transformedConflicts
    if (this.conflictSelectedInReview) {
      this.currentConflictIndex = 0;
      this.conflictSelectedInReview = null;
    }
    this.selectPath()
  }

  selectPath() {
    const path = (!this.conflictsWithBreadcrumbs[this.currentConflictIndex]?.hasMergeInnerFiles || this.conflictsWithBreadcrumbs[this.currentConflictIndex].filesCopy[0].virtual) ? this.conflictsWithBreadcrumbs[this.currentConflictIndex].breadcrumbs.slice(1) : this.conflictsWithBreadcrumbs[this.currentConflictIndex].breadcrumbs.slice(-1);
    this.onPathSelect(path[0]);
  }

  onPathSelect(breadcrumb: any) {
    const currentConflict = this.conflictsWithBreadcrumbs[this.currentConflictIndex]
    currentConflict.selectedPath = breadcrumb.path
    const match = this.findMatchingFile(currentConflict.selectedPath!, this.conflictsWithBreadcrumbs[this.currentConflictIndex].filesCopy[0])
    currentConflict.files = [{ ...match }]
    this.conflictedFileDiff = null
    if (currentConflict.files[0].pathType === 'FILE' && !currentConflict.files[0].virtual && currentConflict.files[0].latestBranchInType !== "PATH_CONFLICTED") {
      if (currentConflict.files[0].conflictedFileParts?.length) {
        currentConflict.files[0].conflictedFileParts.forEach(filePart => {
          filePart.selectedAction = this.propertyEditoractions[0];
        });
      } else {
        this.getFileDiff(currentConflict.path)
      }
    } else {
      currentConflict.files.forEach(file => {
        this.constructTree(file)
        this.isAllowedToChangeAction(file, true)
      })

      if (currentConflict.files[0].pathType === 'DIR') {
        currentConflict.files.forEach(file => this.expandFolder(file, currentConflict.path));
      }
    }
  }

  private findMatchingFile(path: string, rootFile: IConflictFile): any {
    let match: IConflictFile | null = null;
    if (rootFile.path === path) {
      match = rootFile
      return match
    } else if (rootFile.children && rootFile.children.length) {
      for (let childFile of rootFile.children) {
        match = this.findMatchingFile(path, childFile);
        if (match) {
          break;
        }
      }
    }
    return match;
  }

  constructTree(file: IConflictFile, level?: number) {
    level = level || 1;
    file.level = level;
    if (file?.description) {
      file.description = replace(file.description, this.descriptionMsg) as string;
    }

    if (file.pathType === 'DIR' && file.children && file.children.length > 0) {
      level += 1;
      for (let i = file.children.length; i > 0; i--) {
        this.constructTree(file.children[i - 1], level);
      }
    }
  }

  expandFolder(file: IConflictFile, path: string) {
    if (file.pathType === 'DIR' && path.startsWith(file.path)) {
      file.open = true;
      if (file.children && file.children.length) {
        file.children.forEach(childFile => this.expandFolder(childFile, path));
      }
    }
  }

}
